import React from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import apiURL from './apiURL';
import {UserCountBadge} from './UserTotalCount';

const readContentRangeTotal = response => {
    const range = response.headers.get('content-range') || '';
    const match = range.match(/\/(\d+)\s*$/);

    if (!match) {
        return null;
    }

    const total = Number(match[1]);

    return Number.isFinite(total) ? total : null;
};

export const useAdminResourceTotalCount = (resource, queryString = '') => {
    const [total, setTotal] = React.useState(null);

    /*
     * React Admin incrémente viewVersion après une création,
     * une modification, une suppression ou un refresh.
     *
     * Les compteurs personnalisés suivent cette version afin
     * de rester synchronisés avec les listes admin.
     */
    const viewVersion = useSelector(state =>
        state &&
        state.admin &&
        state.admin.ui
            ? state.admin.ui.viewVersion
            : 0
    );

    /*
     * Les formulaires React Admin redirigent vers la liste après
     * enregistrement. Le changement de route sert également de
     * déclencheur afin que les badges soient à jour dès le retour.
     */
    const location = useLocation();
    const routeVersion = `${location.pathname}${location.search}`;

    React.useEffect(() => {
        let active = true;

        const token = localStorage.getItem('auth') || '';

        const query = [
            'limit=1',
            'skip=0',
            queryString,
        ].filter(Boolean).join('&');

        fetch(`${apiURL}/${resource}?${query}`, {
            method: 'GET',
            cache: 'no-store',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
            },
        })
            .then(async response => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                /*
                 * Certains endpoints admin exposent le total via
                 * Content-Range, d'autres via { data, total }.
                 * On accepte volontairement les deux formats sans
                 * modifier l'API existante.
                 */
                const headerTotal = readContentRangeTotal(response);

                if (headerTotal !== null) {
                    return headerTotal;
                }

                const json = await response.json();
                const bodyTotal =
                    json &&
                    !Array.isArray(json) &&
                    Number(json.total);

                return Number.isFinite(bodyTotal)
                    ? bodyTotal
                    : null;
            })
            .then(value => {
                if (active) {
                    setTotal(value);
                }
            })
            .catch(() => {
                if (active) {
                    setTotal(null);
                }
            });

        return () => {
            active = false;
        };
    }, [resource, queryString, viewVersion, routeVersion]);

    return total;
};

export const ResourceCountLabel = ({label, total}) => (
    <span
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        <span>{label}</span>
        <UserCountBadge total={total} />
    </span>
);

export const ResourceListTitle = ({
    resource,
    label,
    queryString = '',
}) => {
    const total = useAdminResourceTotalCount(resource, queryString);

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
            }}
        >
            <span>{label}</span>
            <UserCountBadge total={total} />
        </span>
    );
};
